import type { ReactNode } from 'react';
import { Icon, type IconName } from './Icon';
import s from './anzeige.module.css';

/** Nicht bedienbarer Tag (GL-023 §9.5). */
export function Tag({ hervorgehoben = false, children }: { hervorgehoben?: boolean; children: ReactNode }) {
  return <span className={`${s.tag} ${hervorgehoben ? s.tagHervor : ''}`}>{children}</span>;
}

/**
 * Badge. "foto": weisses Label auf Bild ("Von Nora" tuerkis, "Importiert" ink).
 * "flaeche": auch Herkunfts-Badge "berechnet"/"geschaetzt" an Formularfeldern.
 */
export function Badge({
  art = 'flaeche',
  ton = 'standard',
  id,
  children,
}: {
  art?: 'foto' | 'flaeche';
  ton?: 'standard' | 'nora';
  id?: string;
  children: ReactNode;
}) {
  const k = art === 'foto' ? `${s.badgeFoto} ${ton === 'nora' ? s.badgeNora : ''}` : s.badgeFlaeche;
  return (
    <span id={id} className={`${s.badge} ${k}`}>
      {children}
    </span>
  );
}

const kreisIcon: Record<'erfolg' | 'fehler' | 'leer', { icon: IconName; strich: number }> = {
  erfolg: { icon: 'haken', strich: 2.5 },
  fehler: { icon: 'ausrufezeichen', strich: 2.5 },
  leer: { icon: 'nora', strich: 1.9 },
};

/** Status-Kreis 60 px (GL-023 §9.10): Erfolg, Fehler, Leerzustand. Keine Warn-Variante. */
export function StatusKreis({ art, icon }: { art: 'erfolg' | 'fehler' | 'leer'; icon?: IconName }) {
  const d = kreisIcon[art];
  return (
    <span aria-hidden="true" className={`${s.kreis} ${s[art]}`}>
      <Icon name={icon ?? d.icon} groesse={24} strich={d.strich} />
    </span>
  );
}

/** Leerzustand nach Canvas R5: Illustration im Kreis (oder Status-Kreis), Titel, Text, Aktionen. */
export function LeerZustand({
  titel,
  text,
  bild,
  icon,
  aktionen,
  titelId,
}: {
  titel: string;
  text: ReactNode;
  bild?: string;
  icon?: IconName;
  aktionen?: ReactNode;
  titelId?: string;
}) {
  return (
    <section className={s.leerzustand} aria-labelledby={titelId}>
      {bild ? (
        <span aria-hidden="true" className={s.leerBild}>
          <img src={bild} alt="" width={176} height={176} decoding="async" />
        </span>
      ) : (
        <StatusKreis art="leer" icon={icon} />
      )}
      <h2 id={titelId} className={s.leerTitel}>
        {titel}
      </h2>
      <p className={s.leerText}>{text}</p>
      {aktionen && <div className={s.leerAktionen}>{aktionen}</div>}
    </section>
  );
}

/** Fortschrittsbalken 4 px (GL-023 §9.10). Animiert nur transform. */
export function Fortschritt({ wert, max, label }: { wert: number; max: number; label: string }) {
  const anteil = max > 0 ? Math.min(1, Math.max(0, wert / max)) : 0;
  return (
    <div role="progressbar" aria-label={label} aria-valuemin={0} aria-valuemax={max} aria-valuenow={wert} className={s.fortschritt}>
      <div className={s.fortschrittFuellung} style={{ transform: `scaleX(${anteil})` }} />
    </div>
  );
}

/** Hinweisblock. Fehler immer mit Icon und Klartext, Warnung ohne Orange-Flaeche (GL-023 §2.3, §9.10). */
export function Hinweis({
  art,
  titel,
  children,
}: {
  art: 'info' | 'fehler' | 'warnung';
  titel?: string;
  children: ReactNode;
}) {
  const klasse = art === 'info' ? s.hinweisInfo : art === 'fehler' ? s.hinweisFehler : s.hinweisWarnung;
  const icon: IconName = art === 'info' ? 'nora' : art === 'fehler' ? 'ausrufezeichen' : 'warnung';
  return (
    <div className={`${s.hinweis} ${klasse}`} role={art === 'fehler' ? 'alert' : undefined}>
      <Icon name={icon} groesse={20} strich={art === 'info' ? 1.8 : 2.2} />
      <div>
        {titel && <span className={s.hinweisTitel}>{titel}</span>}
        {children}
      </div>
    </div>
  );
}

/** Ruhiger Ladezustand ohne Spinner-Gewitter. */
export function Laedt({ text = 'Lädt …' }: { text?: string }) {
  return (
    <div className={s.lade} role="status" aria-live="polite">
      {text}
    </div>
  );
}
